// @ts-check
import React, { useState } from 'react';

import Divider from '@liquidweb-dev/undercurrent/Divider';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import ArrowUpRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowUpRight';
import BadgeIcon from '@liquidweb-dev/undercurrent/icons/lucide/Badge';
import CopyIcon from '@liquidweb-dev/undercurrent/icons/lucide/Copy';
import EllipsisIcon from '@liquidweb-dev/undercurrent/icons/lucide/Ellipsis';
import GlobeIcon from '@liquidweb-dev/undercurrent/icons/lucide/Globe';
import HeadsetIcon from '@liquidweb-dev/undercurrent/icons/lucide/Headset';
import PencilIcon from '@liquidweb-dev/undercurrent/icons/lucide/Pencil';
import PowerIcon from '@liquidweb-dev/undercurrent/icons/lucide/Power';
import RefreshCcwIcon from '@liquidweb-dev/undercurrent/icons/lucide/RefreshCcw';
import RotateCcwIcon from '@liquidweb-dev/undercurrent/icons/lucide/RotateCcw';
import ScalingIcon from '@liquidweb-dev/undercurrent/icons/lucide/Scaling';
import TrashIcon from '@liquidweb-dev/undercurrent/icons/lucide/Trash2';
import ListItemIcon from '@liquidweb-dev/undercurrent/ListItemIcon';
import Menu from '@liquidweb-dev/undercurrent/Menu';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce';
import DialogDestroyServer from 'components/standard/DialogDestroyServer';
import DialogShutdownServer from 'components/standard/DialogShutdownServer';
import DialogStartServer from 'components/standard/DialogStartServer';
import DrawerCloneServer from 'components/standard/DrawerCloneServer';
import DrawerEditServerHostname from 'components/standard/DrawerEditServerHostname';
import DrawerReboot from 'components/standard/DrawerReboot';
import DrawerReimage from 'components/standard/DrawerReimage';
import DrawerSupportAccess from 'components/standard/DrawerSupportAccess';
import DrawerUnblockIp from 'components/standard/DrawerUnblockIp';
import useVmWareVCloudVdcLoginUrl from 'modules/queries/vmWare/vCloud/vdc/useLogin';
import { Link as RouterLink } from 'react-router-dom';
import FavoriteMenuItem from './FavoriteMenuItem';

/**
 * @typedef {'' | 'clone' | 'editHostname' | 'reboot' | 'reimage' | 'shutdown' | 'start' | 'supportAccess' | 'unblockIp' | 'destroy'} ActionKey
 * */

const SArrowUpRight = () => (
	<ArrowUpRightIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);
const SGlobeIcon = () => (
	<GlobeIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);
const SHeadset = () => (
	<HeadsetIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);
const SScaling = () => (
	<ScalingIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);
const SCopy = () => (
	<CopyIcon sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })} />
);
const SRefreshCcw = () => (
	<RefreshCcwIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);
const SPencil = () => (
	<PencilIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);
const SPower = () => (
	<PowerIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);
const STrash = () => (
	<TrashIcon
		sx={(theme) => ({
			height: theme.size.xS,
			width: theme.size.xS,
			color: theme.vars.palette.uc.danger.icon.main,
		})}
	/>
);

/**
 * @param {import('modules/queries/asset/useDetails').AssetDetails & {
 *   intent?: 'serverInterior' | 'serversList',
 *   iconButtonProps?: import('@liquidweb-dev/undercurrent/IconButton').IconButtonProps,
 * }} props
 */
const ActionsContent = ({
	capabilities,
	domain,
	featureDetails,
	ip,
	isVmware,
	powerStatus,
	status,
	uniq_id: uniqId,
	isPrivateParent,
	intent = 'serverInterior',
	iconButtonProps,
}) => {
	const {
		sx: iconButtonSx,
		onClick: iconButtonOnClick,
		...restIconButtonProps
	} = iconButtonProps || {};

	const [actionContentKey, setActionContentKey] = useState(
		/** @type {ActionKey} */ (''),
	);

	const { mutateAsync: fetchVmWareLogin } = useVmWareVCloudVdcLoginUrl();

	const [menuAnchorEl, setMenuAnchorEl] = useState(
		/** @type {HTMLButtonElement | null} */ (null),
	);

	const isActive = status === 'Active';
	const isCpanel = fromApiBoolean(capabilities?.cpanelLogin);
	const isInterworx = fromApiBoolean(capabilities?.interworxLogin);
	const isPlesk = fromApiBoolean(capabilities?.pleskLogin);
	const autoRename = fromApiBoolean(capabilities?.autoRename);
	const manualRename = fromApiBoolean(capabilities?.manualRename);

	const showGoToSupportAccess = !fromApiBoolean(
		capabilities?.credentialsNotCustomerFacing,
	);
	const showEditHostname =
		intent === 'serversList' &&
		!isPrivateParent &&
		(autoRename || manualRename);
	const showResize = fromApiBoolean(capabilities?.resize);
	const showReimage = fromApiBoolean(capabilities?.reimage);
	const showCloneServer = fromApiBoolean(capabilities?.clone);
	const showStartServer = !isPrivateParent && isActive && powerStatus === 'off';
	const showShutdownServer =
		!isPrivateParent && isActive && powerStatus === 'on';
	const showDestroyServer = intent === 'serversList' && isActive;
	const showReboot =
		intent === 'serversList' && !isPrivateParent && powerStatus !== 'unknown';
	const showCpanelLicense = isCpanel && featureDetails?.cPanelLicenseTier;

	const hasTopGroup =
		showGoToSupportAccess ||
		isCpanel ||
		isInterworx ||
		isPlesk ||
		isVmware ||
		showEditHostname;
	const hasBottomGroup =
		showStartServer || showShutdownServer || showDestroyServer;

	const rebootDisabled = !isActive || powerStatus !== 'on';
	const cloneDisabled = !isActive;
	const resizeDisabled = !isActive || powerStatus !== 'on';
	const reimageDisabled = !isActive || powerStatus !== 'on';

	const handleVmWareVCloudVdcLogin = async () => {
		try {
			const data = await fetchVmWareLogin({ uniq_id: uniqId });

			if (data.url) {
				window.open(data.url, '_blank', 'noopener');
			}
		} catch {
			// handled in hook definition
		}
	};

	/** @param {ActionKey} key */
	const handleMenuItemClick = (key) => {
		setMenuAnchorEl(null);
		setActionContentKey(key);
	};

	const handleCloseMenu = () => {
		setMenuAnchorEl(null);
	};

	return (
		<>
			<IconButton
				size="large"
				variant="secondary"
				sx={combineSx({ flexGrow: 1 }, iconButtonSx)}
				onClick={(e) => {
					setMenuAnchorEl(e.currentTarget);
					iconButtonOnClick?.(e);
				}}
				{...restIconButtonProps}
			>
				<EllipsisIcon />
			</IconButton>

			<Menu
				open={Boolean(menuAnchorEl)}
				onClose={() => setMenuAnchorEl(null)}
				anchorEl={menuAnchorEl}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			>
				{showGoToSupportAccess && (
					<MenuItem
						disabled={!isActive}
						onClick={() => handleMenuItemClick('supportAccess')}
					>
						<ListItemIcon>
							<SHeadset />
						</ListItemIcon>
						<Typography variant="label-lg">Support access</Typography>
					</MenuItem>
				)}
				{isCpanel && (
					<MenuItem
						disabled={!isActive}
						onClick={() => {
							window.open(`https://${ip}:2083`, '_blank', 'noopener');
						}}
					>
						<ListItemIcon>
							<SArrowUpRight />
						</ListItemIcon>
						<Typography variant="label-lg">Go to cPanel</Typography>
					</MenuItem>
				)}
				{showCpanelLicense && (
					<MenuItem
						component={RouterLink}
						to={`/servers/${uniqId}/cpanel`}
						disabled={!isActive}
					>
						<ListItemIcon>
							<BadgeIcon />
						</ListItemIcon>
						<Typography variant="label-lg">cPanel License Tier</Typography>
					</MenuItem>
				)}
				{isCpanel && (
					<MenuItem
						disabled={!isActive}
						onClick={() => {
							window.open(`https://${ip}:2087`, '_blank', 'noopener');
						}}
					>
						<ListItemIcon>
							<SArrowUpRight />
						</ListItemIcon>
						<Typography variant="label-lg">Go to WHM</Typography>
					</MenuItem>
				)}
				{isCpanel && (
					<MenuItem
						disabled={!isActive}
						onClick={() => handleMenuItemClick('unblockIp')}
					>
						<ListItemIcon>
							<SGlobeIcon />
						</ListItemIcon>
						<Typography variant="label-lg">Unblock IP</Typography>
					</MenuItem>
				)}
				{isInterworx && (
					<MenuItem
						disabled={!isActive}
						onClick={() => {
							window.open(`https://${ip}:2443`, '_blank', 'noopener');
						}}
					>
						<ListItemIcon>
							<SArrowUpRight />
						</ListItemIcon>
						<Typography variant="label-lg">Go to Interworx</Typography>
					</MenuItem>
				)}
				{isPlesk && (
					<MenuItem
						disabled={!isActive}
						onClick={() => {
							window.open(`https://${ip}:8443`, '_blank', 'noopener');
						}}
					>
						<ListItemIcon>
							<SArrowUpRight />
						</ListItemIcon>
						<Typography variant="label-lg">Go to Plesk</Typography>
					</MenuItem>
				)}
				{isVmware && (
					<MenuItem disabled={!isActive} onClick={handleVmWareVCloudVdcLogin}>
						<ListItemIcon>
							<SArrowUpRight />
						</ListItemIcon>
						<Typography variant="label-lg">Go to VMWare</Typography>
					</MenuItem>
				)}
				{showEditHostname && (
					<MenuItem
						disabled={!isActive}
						onClick={() => handleMenuItemClick('editHostname')}
					>
						<ListItemIcon>
							<SPencil />
						</ListItemIcon>
						<Typography variant="label-lg">Edit Hostname</Typography>
					</MenuItem>
				)}
				{hasTopGroup && <Divider sx={{ marginX: { sm: -50 } }} />}

				<FavoriteMenuItem
					onClick={handleCloseMenu}
					favoritesDisplayName={domain}
					uniqId={uniqId}
				/>
				{showCloneServer && (
					<MenuItem
						disabled={cloneDisabled}
						onClick={() => handleMenuItemClick('clone')}
					>
						<ListItemIcon>
							<SCopy />
						</ListItemIcon>
						<Typography variant="label-lg">Clone server</Typography>
					</MenuItem>
				)}
				{showReboot && (
					<MenuItem
						disabled={rebootDisabled}
						onClick={() => handleMenuItemClick('reboot')}
					>
						<ListItemIcon>
							<RotateCcwIcon />
						</ListItemIcon>
						<Typography variant="label-lg">Reboot</Typography>
					</MenuItem>
				)}
				{showResize && (
					<MenuItem
						disabled={resizeDisabled}
						component={RouterLink}
						to={`/servers/${uniqId}/resize`}
					>
						<ListItemIcon>
							<SScaling />
						</ListItemIcon>
						<Typography variant="label-lg">Resize</Typography>
					</MenuItem>
				)}
				{showReimage && (
					<MenuItem
						disabled={reimageDisabled}
						onClick={() => handleMenuItemClick('reimage')}
					>
						<ListItemIcon>
							<SRefreshCcw />
						</ListItemIcon>
						<Typography variant="label-lg">Reimage</Typography>
					</MenuItem>
				)}
				{hasBottomGroup && <Divider sx={{ marginX: { sm: -50 } }} />}

				{showStartServer && (
					<MenuItem onClick={() => handleMenuItemClick('start')}>
						<ListItemIcon>
							<SPower />
						</ListItemIcon>
						<Typography variant="label-lg">Start server</Typography>
					</MenuItem>
				)}
				{showShutdownServer && (
					<MenuItem onClick={() => handleMenuItemClick('shutdown')}>
						<ListItemIcon>
							<SPower />
						</ListItemIcon>
						<Typography variant="label-lg">Shutdown server</Typography>
					</MenuItem>
				)}
				{showDestroyServer && (
					<MenuItem
						onClick={() => handleMenuItemClick('destroy')}
						sx={{
							':hover, :focus-visible': {
								backgroundColor: ({ vars }) => vars.palette.uc.danger.bg.main,
							},
						}}
					>
						<ListItemIcon>
							<STrash />
						</ListItemIcon>
						<Typography color="uc.danger.icon.strong" variant="label-lg">
							Destroy server
						</Typography>
					</MenuItem>
				)}
			</Menu>

			<DrawerCloneServer
				open={actionContentKey === 'clone'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
			<DrawerReboot
				open={actionContentKey === 'reboot'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
			<DialogShutdownServer
				uniqId={uniqId}
				open={actionContentKey === 'shutdown'}
				onClose={() => setActionContentKey('')}
				domain={domain}
				ip={ip || ''}
			/>
			<DialogStartServer
				uniqId={uniqId}
				open={actionContentKey === 'start'}
				onClose={() => setActionContentKey('')}
				domain={domain}
				ip={ip || ''}
			/>
			<DrawerSupportAccess
				open={actionContentKey === 'supportAccess'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
			<DrawerEditServerHostname
				open={actionContentKey === 'editHostname'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
			<DrawerReimage
				open={actionContentKey === 'reimage'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
			<DrawerUnblockIp
				open={actionContentKey === 'unblockIp'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
			<DialogDestroyServer
				open={actionContentKey === 'destroy'}
				onClose={() => setActionContentKey('')}
				uniqId={uniqId}
			/>
		</>
	);
};

export default ActionsContent;
